/**
 * Centralized runtime config, sourced from Vite env vars (see .env.example).
 * Nothing else in the app should read import.meta.env directly.
 */

interface Config {
  wsUrl: string;
  restUrl: string;
  tileUrlTemplate: string;
  allowSimFallback: boolean;
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string;
  };
}

function req(name: string, value: string | undefined): string {
  if (!value) {
    // Fail loud in dev; in prod a missing var is a deploy misconfiguration.
    console.error(`[config] missing required env var: ${name}`);
    return "";
  }
  return value;
}

const env = import.meta.env;

export const config: Config = {
  wsUrl: req("VITE_WS_URL", env.VITE_WS_URL),
  restUrl: req("VITE_REST_URL", env.VITE_REST_URL),
  tileUrlTemplate: env.VITE_TILE_URL_TEMPLATE ??
    "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}.png",
  allowSimFallback: (env.VITE_ALLOW_SIM_FALLBACK ?? "true") === "true",
  firebase: {
    apiKey: req("VITE_FIREBASE_API_KEY", env.VITE_FIREBASE_API_KEY),
    authDomain: req("VITE_FIREBASE_AUTH_DOMAIN", env.VITE_FIREBASE_AUTH_DOMAIN),
    projectId: req("VITE_FIREBASE_PROJECT_ID", env.VITE_FIREBASE_PROJECT_ID),
    appId: req("VITE_FIREBASE_APP_ID", env.VITE_FIREBASE_APP_ID),
  },
};

/**
 * The marketing apex (earlywire.io) and the dashboard subdomain
 * (app.earlywire.io) are served by the SAME SPA on one Netlify site. These
 * helpers let the apex skip into the app, and the app subdomain skip the home.
 */
export function isAppHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.startsWith("app.");
}

/** Absolute base for cross-linking from the apex to the dashboard subdomain.
 *  Empty (same-origin) on app.*, localhost, and *.netlify.app. At BUILD time (prerender, no
 *  window) it must return the apex value — the prerendered HTML is what earlywire.io serves,
 *  and hydration on the apex computes the same string, so the trees match. */
export function appBaseUrl(): string {
  if (typeof window === "undefined") return "https://app.earlywire.io";
  const h = window.location.hostname;
  if (h === "earlywire.io" || h === "www.earlywire.io") return "https://app.earlywire.io";
  return "";
}
